import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "locales";
import {
  AiFeatureDisabledError,
  fetchMeetingArtifacts,
  formatClock,
  MeetingArtifacts,
  reanalyzeRecording,
} from "lib/aiApi";
import "./style.scss";

interface Props {
  recordingId: string;
  /** Polling interval for pending/running jobs (ms). Default 5000. */
  pollIntervalMs?: number;
  className?: string;
}

const MeetingSummaryCard: React.FC<Props> = ({
  recordingId,
  pollIntervalMs = 5000,
  className,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<MeetingArtifacts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const result = await fetchMeetingArtifacts(recordingId);
      if (!aliveRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!aliveRef.current) return;
      if (err instanceof AiFeatureDisabledError) {
        setDisabled(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [recordingId]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  // Poll while the job is still running.
  useEffect(() => {
    if (!data) return;
    if (data.status === "done" || data.status === "failed") return;
    const id = window.setInterval(load, pollIntervalMs);
    return () => window.clearInterval(id);
  }, [data, load, pollIntervalMs]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const result = await reanalyzeRecording(recordingId);
      if (!aliveRef.current) return;
      // Server returns the previous artifact; the actual rerun is async on
      // the queue. Force `status: "pending"` locally so the polling effect
      // kicks back in instead of treating the stale "done" row as final.
      setData({ ...result, status: "pending" });
      setError(null);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (aliveRef.current) setRetrying(false);
    }
  };

  if (disabled) return null;

  if (loading) {
    return (
      <div className={`coach-summary coach-summary--loading ${className || ""}`}>
        <span className="coach-summary__title">{t("AI summary loading…")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`coach-summary coach-summary--error ${className || ""}`}>
        <span className="coach-summary__title">{t("Couldn’t load summary")}</span>
        <span className="coach-summary__error">{error}</span>
      </div>
    );
  }

  if (!data || data.status === "pending") {
    return (
      <div className={`coach-summary coach-summary--pending ${className || ""}`}>
        <span className="coach-summary__title">📊 {t("AI summary in progress…")}</span>
        <span className="coach-summary__hint">
          {t("The AI is reading the transcript and extracting decisions and action items.")}
        </span>
      </div>
    );
  }

  if (data.status === "failed") {
    return (
      <div className={`coach-summary coach-summary--error ${className || ""}`}>
        <span className="coach-summary__title">{t("Summary failed")}</span>
        {data.error && <span className="coach-summary__error">{data.error}</span>}
        <button className="coach-summary__retry" onClick={handleRetry} disabled={retrying}>
          {retrying ? t("Retrying…") : t("Try again")}
        </button>
      </div>
    );
  }

  return (
    <div className={`coach-summary ${className || ""}`}>
      <header className="coach-summary__header">
        <span className="coach-summary__icon" aria-hidden>📊</span>
        <h3 className="coach-summary__title">{t("AI meeting summary")}</h3>
        {data.durationSec ? (
          <span className="coach-summary__duration">{formatClock(data.durationSec)}</span>
        ) : null}
        <button
          className="coach-summary__retry coach-summary__retry--ghost"
          onClick={handleRetry}
          disabled={retrying}
          title={t("Re-run analysis")}
        >
          {retrying ? "…" : "↻"}
        </button>
      </header>

      {data.teamSummary && (
        <p className="coach-summary__overview">{data.teamSummary}</p>
      )}

      {data.topics.length > 0 && (
        <div className="coach-summary__row">
          <span className="coach-summary__row-label">{t("Topics")}</span>
          <div className="coach-summary__topics">
            {data.topics.map((topic) => (
              <span className="coach-summary__topic" key={topic}>{topic}</span>
            ))}
          </div>
        </div>
      )}

      {data.decisions.length > 0 && (
        <div className="coach-summary__row">
          <span className="coach-summary__row-label">{t("Decisions")}</span>
          <ul className="coach-summary__list">
            {data.decisions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      {data.actionItems.length > 0 && (
        <div className="coach-summary__row">
          <span className="coach-summary__row-label">{t("Action items")}</span>
          <ul className="coach-summary__list coach-summary__list--actions">
            {data.actionItems.map((a, i) => (
              <li key={i}>
                {a.assignee && <strong>{a.assignee}: </strong>}
                {a.task}
                {a.dueDate && <em className="coach-summary__due"> ({a.dueDate})</em>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.participants.length > 0 && (
        <div className="coach-summary__row">
          <span className="coach-summary__row-label">{t("Speaking time")}</span>
          <div className="coach-summary__bars">
            {data.participants.map((p) => (
              <div className="coach-summary__bar-row" key={p.userId || p.screenName || ""}>
                <span className="coach-summary__bar-name">
                  {p.screenName || t("User {{id}}", { id: p.userId })}
                </span>
                <div className="coach-summary__bar-track">
                  <div
                    className="coach-summary__bar-fill"
                    style={{ width: `${Math.min(100, p.speakingPct)}%` }}
                  />
                </div>
                <span className="coach-summary__bar-pct">{p.speakingPct.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingSummaryCard;
